import {
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { OnChangeParams, Option } from 'baseui/select';
import { ApiError, PaginationMeta } from 'common/repositories/common.model';
import { queryClient } from 'common/repositories/query-client';
import { camelizeKeys } from 'humps';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useDebouncedCallback } from 'use-debounce';

import useGetLabelSelect from './use-get-label-select';

interface Options<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
> {
  useListQueryHook: (
    baseInputs: ListQueryVariables,
    baseOptions?: UseQueryOptions<ListQueryData, ApiError>,
  ) => UseQueryResult<ListQueryData, ApiError>;
  listBaseOptions?: UseQueryOptions<ListQueryData, ApiError>;
  useDetailLazyQueryHook: (
    baseInputs: DetailQueryVariables,
    baseOptions?: UseQueryOptions<DetailQueryData, ApiError>,
  ) => UseQueryResult<DetailQueryData, ApiError>;
  detailBaseOptions?: UseQueryOptions<DetailQueryData, ApiError>;
  listTransformer: (result: ListQueryData) => Option[];
  detailTransformer: (result: DetailQueryData) => Option;
  paginationTransformer: (result: ListQueryData) => PaginationMeta;
  getMemoizedListVariables: (
    page: number,
    query?: string,
  ) => ListQueryVariables;
  onSelectItem: (value: Option) => void;
  getMemoizedDetailVariables: (value: string) => DetailQueryVariables;
  resetPageWhenQueryChanged?: boolean;
  renderCreate?: boolean;
  onClickCreate?: () => void;
  createText?: string;
  value?: string;
  listKeyFunction?: Function;
  detailKeyFunction?: Function;
  staleTime?: number;
  enabled?: boolean;
}

const SELECT_DEFAULT_STALE_TIME = 60 * 1000;

/** Note: If state keeps on being undefined, try checking if your list/detail query hook accepts options?.queryKey as an alternative to the default key function. useSelectInputHelper injects the key through the options parameter. */
export function getDataFromQueryCache<T>(
  key: QueryKey,
  staleTime: number = SELECT_DEFAULT_STALE_TIME,
): T | undefined {
  const state = queryClient.getQueryState(key);
  const foundNoCachedData = !state || state.data === undefined;
  const dataIsStale = Date.now() - (state?.dataUpdatedAt || 0) > staleTime;
  const dataIsInvalidated = state?.isInvalidated;

  return foundNoCachedData || dataIsStale || dataIsInvalidated
    ? undefined
    : (state.data as T);
}

export default function useSelectInputHelper<
  ListQueryData,
  ListQueryVariables,
  DetailQueryData,
  DetailQueryVariables,
>(
  helperOptions: Options<
    ListQueryData,
    ListQueryVariables,
    DetailQueryData,
    DetailQueryVariables
  >,
) {
  const { t } = useTranslation();
  const {
    renderCreate = false,
    createText = t('common:create_new'),
    onClickCreate = () => {},
    onSelectItem,
    useListQueryHook,
    useDetailLazyQueryHook,
    listTransformer,
    detailTransformer,
    paginationTransformer,
    getMemoizedListVariables,
    getMemoizedDetailVariables,
    value,
    resetPageWhenQueryChanged,
    detailBaseOptions,
    detailKeyFunction,
    listBaseOptions,
    enabled = true,
    listKeyFunction,
    staleTime = SELECT_DEFAULT_STALE_TIME,
  } = helperOptions;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [options, setOptions] = React.useState<Option[]>([]);
  const [selectedOption, setSelectedOption] = React.useState<
    Option | undefined
  >(undefined);
  const [query, setQuery] = React.useState<string | undefined>();
  const [page, setPage] = React.useState(1);

  /* Variables/keys for query */
  const listVariables = React.useMemo(
    () => getMemoizedListVariables(page, query),
    [getMemoizedListVariables, page, query],
  );

  const listKey = React.useMemo(
    () => listKeyFunction?.(listVariables),
    [listKeyFunction, listVariables],
  );

  const detailVariables = React.useMemo(
    () => (value == null ? undefined : getMemoizedDetailVariables(value)),
    [value, getMemoizedDetailVariables],
  );
  const detailKey = React.useMemo(() => {
    return detailVariables && detailKeyFunction?.(detailVariables);
  }, [detailKeyFunction, detailVariables]);

  /* Is data in cache or not? */
  // Causes infinite rerender if the transformer functions are not memoized. Don't know why. Might be because of the setOptions/setSelectedOption being called every render.
  const isInListCache = React.useMemo(() => {
    if (!listKey) return false;

    const cacheNetwork = getDataFromQueryCache<ListQueryData>(
      listKey,
      staleTime,
    );

    if (!cacheNetwork) return false;

    const list = listTransformer(
      camelizeKeys(cacheNetwork as any) as unknown as ListQueryData,
    );
    if (page === 1) {
      setOptions(list);
    } else {
      setOptions((prev) => prev.concat(list));
    }
    return true;
  }, [listKey, staleTime, listTransformer, page]);

  const isInDetailCache = React.useMemo(() => {
    if (!detailKey) return false;

    const cacheNetwork = getDataFromQueryCache<DetailQueryData>(
      detailKey,
      staleTime,
    );

    if (!cacheNetwork) return false;
    const _result = detailTransformer(cacheNetwork);
    setSelectedOption(_result);
    return true;
  }, [detailKey, detailTransformer, staleTime]);

  const {
    isLoading: listLoading,
    data: list,
    error,
    refetch,
  } = useListQueryHook(listVariables, {
    enabled: enabled && !isInListCache,
    queryKey: listKey,
    staleTime: listKeyFunction ? staleTime : undefined,
    cacheTime: listKeyFunction ? staleTime * 2 : undefined,

    onSuccess: (result) => {
      if (page === 1) {
        setOptions(
          listTransformer(
            camelizeKeys(result as any) as unknown as ListQueryData,
          ),
        );
      } else {
        setOptions((prevOptions) =>
          prevOptions.concat(
            listTransformer(
              camelizeKeys(result as any) as unknown as ListQueryData,
            ),
          ),
        );
      }
    },
    ...listBaseOptions,
  });

  const {
    isFetching: detailLoading,
    error: detailError,
    refetch: detailRefetch,
  } = useDetailLazyQueryHook(detailVariables!, {
    ...detailBaseOptions,
    enabled: !!value && !isInDetailCache,
    staleTime: detailKeyFunction ? staleTime : undefined,
    cacheTime: listKeyFunction ? staleTime * 2 : undefined,
    queryKey: detailKey,
    onSuccess: (result) => {
      setSelectedOption(detailTransformer(result as DetailQueryData));
    },
  });

  React.useEffect(() => {
    if (!value) {
      setSelectedOption(undefined);
    }
  }, [value, getMemoizedDetailVariables]);

  const loading = React.useMemo(() => {
    if (value) return detailLoading || listLoading;
    return listLoading;
  }, [detailLoading, listLoading, value]);

  const onMenuOpen = React.useCallback(() => {
    setQuery(undefined);
    if (error) {
      refetch();
    }

    if (detailError) {
      detailRefetch();
    }
  }, [detailError, detailRefetch, error, refetch]);

  const items = React.useMemo(() => {
    const _options = [
      ...(selectedOption
        ? [selectedOption].concat([
            ...options.filter((o) => o.id !== selectedOption?.id),
          ])
        : options),
    ];

    const pagination = list && paginationTransformer(list);
    if (pagination && pagination.currentPage < pagination.lastPage) {
      _options.push({
        label: 'Load More...',
        id: 'actionLoad',
        disabled: true,
      });
    }

    if (renderCreate) {
      _options.push({
        label: createText,
        id: 'actionCreate',
        disabled: true,
      });
    }

    return _options;
  }, [
    createText,
    list,
    options,
    paginationTransformer,
    renderCreate,
    selectedOption,
  ]);

  const onQueryChange = useDebouncedCallback(
    (event) => {
      resetPageWhenQueryChanged && setPage(1);
      setQuery(event?.target?.value || '');
    },
    800,
    {
      trailing: true,
    },
  );

  const onFocus = React.useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const pagination = list && paginationTransformer(list);

  const _onChange = React.useCallback(
    (params: OnChangeParams) => {
      onSelectItem(params.value);
      setIsMenuOpen(false);
    },
    [onSelectItem],
  );

  const _value = React.useMemo(() => {
    if (value) {
      const currentSelected = items.find((find) => find.id === value);

      if (currentSelected) {
        return [currentSelected];
      } else {
        return selectedOption ? [selectedOption] : undefined;
      }
    } else {
      return undefined;
    }
  }, [items, selectedOption, value]);

  const getLabel = useGetLabelSelect({
    setIsMenuOpen,
    loading,
    onClickCreate,
    pagination,
    setPage,
  });

  return {
    value: _value,
    options: items,
    isLoading: loading,
    onOpen: onMenuOpen,
    onInputChange: onQueryChange,
    onFocus,
    getOptionLabel: getLabel as any,
    getValueLabel: getLabel,
    onChange: _onChange,
    closeOnSelect: false,
    clearable: true,
    maxDropdownHeight: '300px',
    overrides: {
      Popover: {
        props: {
          isOpen: isMenuOpen,
          onClickOutside: () => setIsMenuOpen(false),
          onBlur: () => setIsMenuOpen(false),
          overrides: {
            Body: {
              style: {
                zIndex: 1000,
              },
            },
          },
        },
      },
    },
  };
}
